'use client';

import * as ENGINE from '@gnsx/genesys.js';
import * as THREE from 'three';

import { AssetType } from '../assetTypes';

import { thumbnailSubtreeHasRenderableMesh } from './prefabThumbnailReadiness';

import type { IAssetFile } from '@/editor/store/models';

import { asyncLoadPrefabAsset } from '@/editor/core/actions/prefabActions';
import { resolvePreviewToneMapping } from '@/editor/core/preview/resolvePreviewToneMapping';

const DEBUG_LOG = {
  RenderingThumbnail: false,
  PrefabBounds: false,
} as const;


/** Thumbnail size */
const THUMBNAIL_SIZE = 128;
export type ThumbnailRendererType = 'webgl' | 'webgpu';

/**
 * Small viewport + filmic tone mapping reads darker than the main editor view;
 * nudge exposure so asset icons stay readable without changing user TM prefs.
 */
const THUMBNAIL_TONE_MAPPING_EXPOSURE_SCALE = 1.12;

/** Subtle transmissive backdrop gradient (left -> right: pale blue to white). */
const TRANSMISSIVE_BACKDROP_LEFT_COLOR = '#363b40';
const TRANSMISSIVE_BACKDROP_RIGHT_COLOR = '#ffffff';

/** Fallback bounds for objects - minimal size for maximum thumbnail fill */
const fallbackUnitBounds = new THREE.Box3().setFromCenterAndSize(
  new THREE.Vector3(0, 0, 0),
  new THREE.Vector3(0.1, 0.1, 0.1)
);

/** Matches InspectorPreview prefab/model fallback when bounds are empty */
const inspectorPreviewFallbackBounds = new THREE.Box3().setFromCenterAndSize(
  new THREE.Vector3(0, 0, 0),
  new THREE.Vector3(1, 1, 1)
);

/** Dynamic padding based on object size and asset type - matches InspectorPreview */
const getDynamicFitPadding = (bounds: THREE.Box3, assetType: AssetType) => {
  const size = bounds.getSize(new THREE.Vector3());
  const maxDimension = Math.max(size.x, size.y, size.z);

  let paddingRatio: number;

  if (assetType === AssetType.Material) {
    // Materials (spheres) use minimal padding like InspectorPreview
    paddingRatio = 0.001; // Fixed padding for materials like InspectorPreview
  } else {
    // Models and prefabs use similar padding to InspectorPreview
    paddingRatio = Math.max(0.05, Math.min(0.25, maxDimension * 0.15));
  }

  return {
    paddingTop: paddingRatio,
    paddingBottom: paddingRatio,
    paddingLeft: paddingRatio,
    paddingRight: paddingRatio
  };
};

/**
 * Headless thumbnail renderer using Three.js
 * Generates 128x128 WebP thumbnails for 3D assets
 */
export class ThumbnailRenderer {

  /** Whether the renderer is initialised (guard against double init) */
  private initialised = false;

  /**
   * Actor used for prefab thumbnail camera framing ({@link editorUtils.getActorBounds}).
   * Cleared after each {@link generateThumbnail} and in {@link clearScene}.
   */
  private prefabActorForThumbnail: ENGINE.Actor | null = null;

  /** Three.js components */
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: ENGINE.IGenesysRenderer;
  private rendererReady: Promise<void> = Promise.resolve();
  private canvas!: HTMLCanvasElement;
  private readonly requestedRendererType: ThumbnailRendererType;

  /** Lighting setup */
  private ambientLight!: THREE.AmbientLight;
  private directionalLight!: THREE.DirectionalLight;
  private fillLight!: THREE.DirectionalLight;
  private rimLight!: THREE.DirectionalLight;

  /** Environment mapping */
  private environmentTexture: THREE.Texture | null = null;
  private transmissiveBackdropTexture: THREE.Texture | null = null;
  private pmremGenerator: THREE.PMREMGenerator | null = null;
  private environmentReady: Promise<void> = Promise.resolve();
  private isDisposed = false;

  constructor(rendererType: ThumbnailRendererType = 'webgpu') {
    this.requestedRendererType = rendererType;
    this.rendererReady = this.initializeRenderer();
  }

  getRequestedRendererType(): ThumbnailRendererType {
    return this.requestedRendererType;
  }

  /** Initialize Three.js renderer (guarded: no-op if already initialised) */
  private async initializeRenderer(): Promise<void> {
    if (this.initialised) return;
    this.isDisposed = false;

    // Create offscreen canvas
    this.canvas = document.createElement('canvas');
    this.canvas.width = THUMBNAIL_SIZE;
    this.canvas.height = THUMBNAIL_SIZE;

    // Initialize backend-specific renderer.
    if (this.requestedRendererType === 'webgpu') {
      this.renderer = await ENGINE.GenesysWebGPURenderer.create({
        canvas: this.canvas,
        antialias: true,
        alpha: true,
        depth: true,
        stencil: false,
        powerPreference: 'high-performance',
      });
    } else {
      this.renderer = await ENGINE.GenesysWebGLRenderer.create({
        canvas: this.canvas,
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      });
    }

    const nativeRenderer = this.renderer.asWebGL() || this.renderer.asWebGPU();
    this.renderer.setSize(THUMBNAIL_SIZE, THUMBNAIL_SIZE);
    nativeRenderer?.setClearColor(0x000000, 0); // Transparent background

    const initialTm = resolvePreviewToneMapping('scene', null);
    this.renderer.toneMapping = initialTm.mode;
    this.renderer.toneMappingExposure =
      initialTm.exposure * THUMBNAIL_TONE_MAPPING_EXPOSURE_SCALE;
    if (nativeRenderer) {
      nativeRenderer.outputColorSpace = THREE.SRGBColorSpace;
    }

    // PMREMGenerator relies on WebGL ShaderMaterial internals and is incompatible with WebGPU's NodeMaterial path.
    // For WebGPU thumbnails, we'll use the generated equirect texture directly.
    if (!this.renderer.asWebGPU()) {
      this.pmremGenerator = new THREE.PMREMGenerator(nativeRenderer as THREE.WebGLRenderer);
      this.pmremGenerator.compileEquirectangularShader();
    } else {
      this.pmremGenerator = null;
    }

    // Initialize camera
    this.camera = new THREE.PerspectiveCamera(
      60, // FOV
      1,  // Aspect ratio (square)
      0.1, // Near
      1000 // Far
    );

    // Initialize scene
    this.scene = new THREE.Scene();

    // Setup lighting and environment
    this.setupLighting();
    this.environmentReady = this.setupEnvironment();

    this.initialised = true;
  }

  /** Setup lighting for thumbnails - studio-style fill so small previews read clearly */
  private setupLighting(): void {
    // Ambient light for overall fill
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.72);
    this.scene.add(this.ambientLight);

    // Key light (main directional light) - Rembrandt style positioning
    // Positioned high and to the side for dramatic lighting
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 1.15);
    this.directionalLight.position.set(2.5, 2.5, 2.5);

    this.scene.add(this.directionalLight);

    // Fill light from opposite side - softer secondary illumination
    this.fillLight = new THREE.DirectionalLight(0xffffff, 0.48);
    this.fillLight.position.set(-2.5, 1, -2.5);
    this.scene.add(this.fillLight);

    // Rim light from above for edge definition
    this.rimLight = new THREE.DirectionalLight(0xffffff, 0.34);
    this.rimLight.position.set(0, 5, 0);
    this.scene.add(this.rimLight);
  }

  /** Setup environment mapping - neutral studio environment for accurate material colors */
  private async setupEnvironment(): Promise<void> {
    await this.rendererReady;
    if (this.isDisposed) return;

    try {
      // Create a neutral studio environment that preserves material colors
      // This ensures chrome appears silver, gold appears gold, etc.
      const environmentTexture = this.createNeutralEnvironment();

      const envMap = this.generateEnvironmentMap(environmentTexture);

      if (this.isDisposed) {
        envMap.dispose();
        return;
      }

      // Set as scene environment for automatic material mapping
      // Lower intensity to preserve material colors while still providing reflections
      this.scene.environment = envMap;
      this.scene.environmentIntensity = 0.68;
      this.environmentTexture = envMap;

      DEBUG_LOG.RenderingThumbnail && console.log('✅ Environment mapping setup complete');
    } catch (error) {
      console.warn('❌ Error setting up environment mapping:', error);
      if (this.isDisposed) return;
      // Create fallback environment
      const fallbackEnv = this.createNeutralEnvironment();
      const envMap = this.generateEnvironmentMap(fallbackEnv);
      if (this.isDisposed) {
        envMap.dispose();
        return;
      }
      this.scene.environment = envMap;
      this.scene.environmentIntensity = 0.68;
      this.environmentTexture = envMap;
    }
  }

  /** Create a neutral studio environment that preserves material colors */
  private createNeutralEnvironment(): THREE.Texture {
    const canvas = document.createElement('canvas');
    canvas.width = 1024; // Higher resolution for better quality
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    // Create neutral studio gradient - preserves material colors
    const gradient = ctx.createLinearGradient(0, canvas.height, 0, 0);
    gradient.addColorStop(0, '#f4f4f4'); // Bottom: light gray
    gradient.addColorStop(0.3, '#ececec'); // Lower middle
    gradient.addColorStop(0.7, '#e0e0e0'); // Upper middle
    gradient.addColorStop(1, '#d6d6d6'); // Top (still neutral, slightly brighter for IBL)

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add subtle variation for realistic reflections without color bias
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const noise = (Math.random() - 0.5) * 8; // Small noise value
      data[i] = Math.max(0, Math.min(255, data[i] + noise)); // R
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise)); // G
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise)); // B
    }

    ctx.putImageData(imageData, 0, 0);

    const texture = new THREE.CanvasTexture(canvas);
    texture.mapping = THREE.EquirectangularReflectionMapping;
    texture.format = THREE.RGBAFormat;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    return texture;
  }

  /**
   * Build the scene environment map from an equirect source texture.
   * WebGL uses PMREM for physically-correct filtering; WebGPU uses the source map directly.
   */
  private generateEnvironmentMap(source: THREE.Texture): THREE.Texture {
    if (!this.renderer.asWebGPU()) {
      const envMap = this.pmremGenerator?.fromEquirectangular(source).texture;
      source.dispose();
      if (!envMap) {
        throw new Error('PMREM generator is not initialized for WebGL thumbnail renderer');
      }
      return envMap;
    }

    source.needsUpdate = true;
    return source;
  }

  /** Generate thumbnail for asset */
  async generateThumbnail(
    asset: IAssetFile,
    quality: number = 85,
    toneMapping: { mode: THREE.ToneMapping; exposure: number },
  ): Promise<Blob> {
    await this.rendererReady;
    if (!this.initialised) {
      throw new Error('ThumbnailRenderer is not initialised');
    }
    if (this.isDisposed) {
      throw new Error('ThumbnailRenderer is disposed');
    }

    await this.environmentReady;
    if (this.isDisposed) {
      throw new Error('ThumbnailRenderer is disposed');
    }

    this.renderer.toneMapping = toneMapping.mode;
    this.renderer.toneMappingExposure =
      toneMapping.exposure * THUMBNAIL_TONE_MAPPING_EXPOSURE_SCALE;

    let addedObject: THREE.Object3D | null = null;

    try {
      // Clear scene and reset camera
      this.clearScene();

      // Load and add asset to scene
      const object = await this.loadAsset(asset);
      if (!object) {
        throw new Error(`Failed to load asset: ${asset.path}`);
      }

      // Keep reference for cleanup
      addedObject = object;
      this.scene.add(object);
      this.prepareSceneForRenderer();

      // Position camera to frame the object
      this.positionCamera(object, asset.assetType);

      // Validate camera position
      if (this.camera.position.length() === 0) {
        throw new Error('Camera position is invalid');
      }

      // Log scene state before rendering
      DEBUG_LOG.RenderingThumbnail &&
        console.log(`🎨 Rendering thumbnail for ${asset.path}:`, {
          sceneChildren: this.scene.children.length,
          cameraPosition: this.camera.position,
          hasLights: this.scene.children.filter(child => child instanceof THREE.Light).length
        });

      const removeTransmissiveBackdrop = this.addTransmissiveBackdropIfNeeded(asset.assetType, object);
      try {
        this.renderer.render(this.scene, this.camera);
        if (asset.assetType === AssetType.Material) {
          await this.waitForRenderStabilization();
          this.renderer.render(this.scene, this.camera);
        }
      } finally {
        removeTransmissiveBackdrop();
      }

      // Convert to blob with timeout
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Thumbnail generation timed out'));
        }, 5000); // 5 second timeout

        this.canvas.toBlob((blob) => {
          clearTimeout(timeout);
          if (blob && blob.size > 0) {
            resolve(blob);
          } else {
            reject(new Error('Failed to generate thumbnail blob or blob is empty'));
          }
        }, 'image/webp', quality / 100);
      });

    } catch (error) {
      DEBUG_LOG.RenderingThumbnail && console.warn(`❌ Error generating thumbnail for ${asset.path}:`, error);

      // Cleanup on error
      if (addedObject && this.scene.children.includes(addedObject)) {
        this.scene.remove(addedObject);
      }

      throw error; // Re-throw for service to handle
    } finally {
      this.prefabActorForThumbnail = null;
    }
  }

  /** Give async GPU/material work one tick before final one-shot capture. */
  private async waitForRenderStabilization(): Promise<void> {
    await new Promise<void>(resolve => setTimeout(resolve, 0));
  }

  /** Apply backend-specific scene fixes before rendering. */
  private prepareSceneForRenderer(): void {
    if (this.renderer.asWebGPU()) {
      this.ensureThumbnailWebGpuUvFallback(this.scene);
    }
  }

  /**
   * Add a gradient plane behind transmissive materials so refraction samples real geometry.
   * This avoids the "flat gray" result that can happen with scene.background-only backdrops.
   */
  private addTransmissiveBackdropIfNeeded(assetType: AssetType, object: THREE.Object3D): () => void {
    if (assetType !== AssetType.Material || !this.hasTransmissiveMaterial(object)) {
      return () => { };
    }

    const bounds = this.calculateBounds(object);
    const center = bounds.getCenter(new THREE.Vector3());
    const radius = Math.max(bounds.getBoundingSphere(new THREE.Sphere()).radius, 0.5);

    const backdropGeometry = new THREE.PlaneGeometry(1, 1);
    const backdropMaterial = new THREE.MeshBasicMaterial({
      map: this.getTransmissiveBackdropTexture(),
      toneMapped: false,
      transparent: false,
      depthWrite: true,
      depthTest: true,
    });
    const backdrop = new THREE.Mesh(backdropGeometry, backdropMaterial);
    backdrop.userData.thumbnailOwnsResources = true;
    backdrop.name = 'TransmissiveThumbnailGradientBackdrop';

    const eyeToCenter = new THREE.Vector3().subVectors(center, this.camera.position).normalize();
    const behindOffset = radius * 2.5;
    backdrop.position.copy(center).addScaledVector(eyeToCenter, behindOffset);
    backdrop.lookAt(this.camera.position);

    const distanceToCamera = this.camera.position.distanceTo(backdrop.position);
    const verticalFovRadians = THREE.MathUtils.degToRad(this.camera.fov);
    const halfHeight = Math.tan(verticalFovRadians / 2) * distanceToCamera;
    const halfWidth = halfHeight * this.camera.aspect;
    const margin = 1.2;
    backdrop.scale.set(halfWidth * 2 * margin, halfHeight * 2 * margin, 1);

    this.scene.add(backdrop);

    return () => {
      this.scene.remove(backdrop);
      this.disposeOwnedObjectResources(backdrop);
    };
  }

  /** Lazily create and cache the subtle transmissive backdrop gradient texture. */
  private getTransmissiveBackdropTexture(): THREE.Texture {
    if (this.transmissiveBackdropTexture) {
      return this.transmissiveBackdropTexture;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to create transmissive backdrop canvas context');
    }

    const gradient = ctx.createLinearGradient(0, canvas.height, 0, 0); // bottom to top
    gradient.addColorStop(0, TRANSMISSIVE_BACKDROP_LEFT_COLOR);
    gradient.addColorStop(1, TRANSMISSIVE_BACKDROP_RIGHT_COLOR);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;
    this.transmissiveBackdropTexture = texture;

    return texture;
  }

  /** Check whether object contains any transmissive material that needs a background. */
  private hasTransmissiveMaterial(root: THREE.Object3D): boolean {
    let foundTransmissiveMaterial = false;

    root.traverse(object => {
      if (foundTransmissiveMaterial || !(object instanceof THREE.Mesh)) {
        return;
      }

      const materials = Array.isArray(object.material) ? object.material : [object.material];
      foundTransmissiveMaterial = materials.some(material => this.isTransmissiveMaterial(material));
    });

    return foundTransmissiveMaterial;
  }

  /** Determine if a material is likely to render white without a background to refract through. */
  private isTransmissiveMaterial(material: THREE.Material | null | undefined): boolean {
    if (!material) {
      return false;
    }

    const maybePhysicalMaterial = material as THREE.Material & {
      transmission?: number;
      _transmission?: number;
    };

    return (maybePhysicalMaterial.transmission ?? maybePhysicalMaterial._transmission ?? 0) > 0;
  }

  /**
   * Ensure a `uv` attribute exists when materials expect it (avoids AttributeNode warnings).
   * Normalized int32/uint32 + deinterleave for WebGPU is handled at GLTF load in the engine.
   */
  private ensureDefaultUvForWebGpuThumbnail(geometry: THREE.BufferGeometry): void {
    if (geometry.getAttribute('uv')) {
      return;
    }
    const position = geometry.getAttribute('position');
    if (!position) {
      return;
    }
    const count = position.count;
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(count * 2), 2));
  }

  /** Headless WebGPU thumbnails: add dummy UVs where missing so node materials do not warn. */
  private ensureThumbnailWebGpuUvFallback(root: THREE.Object3D): void {
    root.traverse(object => {
      if (!(object instanceof THREE.Mesh) || !object.geometry) {
        return;
      }
      this.ensureDefaultUvForWebGpuThumbnail(object.geometry);
    });
  }

  /** Load asset based on type */
  private async loadAsset(asset: IAssetFile): Promise<THREE.Object3D | null> {
    switch (asset.assetType) {
      case AssetType.Model:
        return this.loadModel(asset);
      case AssetType.Prefab:
        return this.loadPrefab(asset);
      case AssetType.Material:
        return this.loadMaterial(asset);
      default:
        throw new Error(`Unsupported asset type for thumbnail: ${asset.assetType}`);
    }
  }

  /** Load model asset */
  private async loadModel(asset: IAssetFile): Promise<THREE.Object3D | null> {
    try {
      const gltf = await ENGINE.resourceManager.loadModel(ENGINE.AssetPath.fromString(asset.path));
      if (gltf?.scene) {
        // Clone the scene to avoid modifying the cached version
        const clonedScene = gltf.scene.clone();
        if (clonedScene.children.length === 0) {
          throw new Error('Model scene is empty');
        }
        return clonedScene;
      }
      throw new Error('Model GLTF scene is null or undefined');
    } catch (error) {
      console.warn(`Failed to load model ${asset.path}:`, error);
      return null;
    }
  }

  /** Load prefab asset */
  private async loadPrefab(asset: IAssetFile): Promise<THREE.Object3D | null> {
    try {
      // Same async instantiation path as InspectorPreview so nested prefabs resolve via storage.
      const actor = await asyncLoadPrefabAsset(asset.path);
      if (!actor) {
        throw new Error('Failed to load prefab actor');
      }

      this.prefabActorForThumbnail = actor;

      const rootComponent = actor.rootComponent;
      if (!rootComponent) {
        throw new Error('Prefab actor has no root component');
      }

      await this.waitForPrefabRenderables(actor);

      DEBUG_LOG.RenderingThumbnail && console.log(`🧩 Prefab loaded for thumbnail: ${asset.path}`, {
        actor: actor.uuid,
        rootComponent: rootComponent.uuid,
        hasChildren: rootComponent.children.length > 0,
        gltfComponents: actor.getComponents(ENGINE.ModelMeshComponent).length
      });

      return rootComponent;
    } catch (error) {
      console.warn(`Failed to load prefab ${asset.path}:`, error);
      return null;
    }
  }

  /**
   * Wait for prefab async-loadable components and then poll until renderable mesh data exists.
   * This handles non-GLTF renderables and nested prefab composition consistently.
   */
  private async waitForPrefabRenderables(actor: ENGINE.Actor, maxWaitMs: number = 10_000): Promise<void> {
    const deadline = Date.now() + maxWaitMs;
    const timeLeft = (): number => Math.max(0, deadline - Date.now());

    const loadsDone = actor.waitForComponentsToLoad().then(() => { });
    await Promise.race([loadsDone, new Promise<void>(resolve => setTimeout(resolve, timeLeft()))]);

    while (Date.now() < deadline) {
      actor.rootComponent.updateMatrixWorld(true);
      if (thumbnailSubtreeHasRenderableMesh(actor.rootComponent)) {
        return;
      }
      await new Promise<void>(resolve => setTimeout(resolve, 16));
    }
  }

  /** Load material asset */
  private async loadMaterial(asset: IAssetFile): Promise<THREE.Object3D | null> {
    try {
      const material = await ENGINE.resourceManager.loadMaterial(ENGINE.AssetPath.fromString(asset.path));
      if (material) {
        await this.waitForMaterialTextures(material);

        // Create a sphere to display the material
        const geometry = new THREE.SphereGeometry(1, 32, 32);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.userData.thumbnailOwnsResources = true;

        // Slight rotation for better material display
        mesh.rotation.x = THREE.MathUtils.degToRad(15);

        return mesh;
      }
      throw new Error('Material is null or undefined');
    } catch (error) {
      console.warn(`Failed to load material ${asset.path}:`, error);
      return null;
    }
  }

  /**
   * Material thumbnails are one-shot renders. Wait for async UrlTexture-backed assets so we
   * don't render a placeholder frame (black) while images are still loading.
   */
  private async waitForMaterialTextures(material: THREE.Material): Promise<void> {
    const pending: Promise<unknown>[] = [];
    const seen = new Set<THREE.Texture>();
    const visitedObjects = new WeakSet<object>();

    const queueTexture = (value: unknown): void => {
      if (!(value instanceof THREE.Texture) || seen.has(value)) {
        return;
      }
      seen.add(value);

      const maybeUrlTexture = value as THREE.Texture & { loadPromise?: Promise<THREE.Texture | null> };
      if (maybeUrlTexture.loadPromise) {
        pending.push(maybeUrlTexture.loadPromise.catch(() => null));
      }
    };

    const traverseValue = (value: unknown, depth: number): void => {
      if (value == null) return;
      queueTexture(value);
      if (depth > 6 || typeof value !== 'object') return;

      if (Array.isArray(value)) {
        value.forEach(item => traverseValue(item, depth + 1));
        return;
      }

      if (value instanceof Set) {
        value.forEach(item => traverseValue(item, depth + 1));
        return;
      }

      if (value instanceof Map) {
        value.forEach(item => traverseValue(item, depth + 1));
        return;
      }

      const asObject = value as object;
      if (visitedObjects.has(asObject)) {
        return;
      }
      visitedObjects.add(asObject);

      const source = value as { [key: string]: unknown; [key: symbol]: unknown };
      for (const key of Reflect.ownKeys(source)) {
        if (key === 'parent' || key === '__proto__') {
          continue;
        }
        try {
          traverseValue(source[key], depth + 1);
        } catch {
          // Some properties can throw on access; skip and continue collecting others.
        }
      }
    };

    traverseValue(material, 0);

    if (pending.length > 0) {
      await Promise.all(pending);
    }
  }

  /** Position camera to frame object using improved fitToBox logic */
  private positionCamera(object: THREE.Object3D, assetType: AssetType): void {
    // Calculate bounding box
    let bounds = this.calculateBounds(object);

    if (assetType === AssetType.Prefab && bounds.isEmpty() && this.prefabActorForThumbnail) {
      DEBUG_LOG.PrefabBounds && console.warn(
        `🧩 Prefab bounds are empty for actor ${this.prefabActorForThumbnail.uuid}, using InspectorPreview-style fallback.`
      );
      bounds = inspectorPreviewFallbackBounds.clone();
    }

    // Use fallback bounds if empty
    if (bounds.isEmpty()) {
      bounds = fallbackUnitBounds;
    }

    // Apply fitToBox logic similar to InspectorPreview
    this.fitCameraToBox(bounds, assetType);
  }

  /** Reset camera to default position and orientation */
  private resetCamera(): void {
    this.camera.position.set(0, 0, 4);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
  }

  /** Fit camera to bounding box with dynamic padding and positioning */
  private fitCameraToBox(bounds: THREE.Box3, assetType: AssetType): void {
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());

    // Get dynamic padding based on object size and asset type
    const padding = getDynamicFitPadding(bounds, assetType);

    // Apply padding to bounds
    const paddedSize = new THREE.Vector3(
      size.x * (1 + padding.paddingLeft + padding.paddingRight),
      size.y * (1 + padding.paddingTop + padding.paddingBottom),
      size.z * (1 + padding.paddingLeft + padding.paddingRight)
    );

    // Calculate distance needed to fit object in view
    const maxDimension = Math.max(paddedSize.x, paddedSize.y, paddedSize.z);
    const distance = maxDimension / (2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2)));

    // Asset-type specific minimum distance - materials get closer for bigger sphere
    const minDistance = assetType === AssetType.Material ? 0.01 : 0.2;
    const cameraDistance = Math.max(distance, minDistance);

    // Position camera directly in front of object (no angle) like InspectorPreview
    // This matches the camera positioning used in InspectorPreview
    this.camera.position.set(
      center.x,
      center.y,
      center.z + cameraDistance
    );

    // Look at center of object
    this.camera.lookAt(center);
    this.camera.updateProjectionMatrix();
  }

  /** Calculate bounding box for object */
  private calculateBounds(object: THREE.Object3D): THREE.Box3 {
    if (this.prefabActorForThumbnail) {
      const actorBounds = this.prefabActorForThumbnail.calcBoundingBox();
      if (!actorBounds.isEmpty()) {
        return actorBounds.clone();
      }
    }

    object.updateMatrixWorld(true);
    const bounds = new THREE.Box3();
    bounds.setFromObject(object);
    return bounds;
  }



  /** Clear scene of all objects except lights */
  private clearScene(): void {
    this.prefabActorForThumbnail = null;

    // More thorough scene clearing
    const objectsToRemove: THREE.Object3D[] = [];

    // Collect all non-light objects
    this.scene.children.forEach((child) => {
      if (!(child instanceof THREE.Light)) {
        objectsToRemove.push(child);
      }
    });

    // Remove all collected objects
    objectsToRemove.forEach((obj) => {
      this.scene.remove(obj);
      this.disposeOwnedObjectResources(obj);
    });

    // Reset camera to default position
    this.resetCamera();
  }

  /** Dispose resources only for objects explicitly owned by this renderer. */
  private disposeOwnedObjectResources(root: THREE.Object3D): void {
    root.traverse(object => {
      if (!(object instanceof THREE.Mesh)) return;
      if (!object.userData.thumbnailOwnsResources) return;

      object.geometry?.dispose();
      if (Array.isArray(object.material)) {
        object.material.forEach(material => material.dispose());
      } else {
        object.material?.dispose();
      }
    });
  }

  /** Dispose of renderer resources and reset state (idempotent) */
  dispose(): void {
    this.isDisposed = true;

    if (this.scene) {
      this.scene.environment = null;
      this.scene.environmentIntensity = 1;
      this.scene.clear();
    }

    if (this.environmentTexture) {
      this.environmentTexture.dispose();
      this.environmentTexture = null;
    }

    if (this.transmissiveBackdropTexture) {
      this.transmissiveBackdropTexture.dispose();
      this.transmissiveBackdropTexture = null;
    }

    if (this.pmremGenerator) {
      this.pmremGenerator.dispose();
    }

    if (this.renderer) {
      this.renderer.dispose();
    }

    this.initialised = false;
  }
}

// ThumbnailRenderer instances are created and managed by ThumbnailService
